import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// clsx
// @ts-ignore
import clsx from 'clsx';

// Material
import AppBarRoot from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import ImgMedia from '@material-ui/core/CardMedia';
import Tooltip from '@material-ui/core/Tooltip';

// Icons
import ExitToApp from '@material-ui/icons/ExitToApp';
import AccountCircle from '@material-ui/icons/AccountCircle';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SettingsIcon from '@material-ui/icons/Settings';

// Constants
import AUTH from 'constants/auth';
import IMAGES from 'constants/images';
import PATH from 'constants/basePath';

// Helpers
import { getClaims, getUserType } from 'helpers/auth';
import { redirectToModule } from 'helpers/redirectToModule';
import { clearStorageSafely } from 'helpers/safeClear';

// Actions
import * as UserActions from 'store/ducks/users/actions';
import * as AppBarActions from 'store/ducks/appBar/actions';
import * as LayoutActions from 'store/ducks/layout/actions';
import * as DrawerActions from 'store/ducks/drawer/actions';

// Icons
import { Book } from 'icons/Miscellaneous';

// Types
import { ApplicationState } from 'store';

// Styles
import useStyles from './styles';

// AppBar Height
const APPBAR_HEIGHT = 64;

interface StateProps {}

interface DispatchProps {}

interface OwnProps {
  clientWidth?: number;
}

type Props = StateProps & DispatchProps & OwnProps;

const AppBar: React.FunctionComponent<Props> = ({ clientWidth }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const SMALL_SCREEN = 960;

  const userData = useSelector(
    (state: ApplicationState) => state.users.firebaseData,
  );

  const drawerOpen = useSelector(
    (state: ApplicationState) => state.drawer.open,
  );

  const appBarVisible = useSelector(
    (state: ApplicationState) => state.appBar.visible,
  );

  const visible = useSelector(
    (state: ApplicationState) => state.layout.visible,
  );

  const listingDrawerOpen = useSelector(
    (state: ApplicationState) => state.listingDrawer.open,
  );

  const companiesList = useSelector(
    (state: ApplicationState) => state.companies.companiesList as any,
  );

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const userType = getUserType();
  const claims = getClaims();
  const open = Boolean(anchorEl);
  const currentPath = history.location.pathname;
  const pathWithLogo = [
    PATH.SELECT_WORKSPACE.BASE_URL,
    PATH.SELECT_WORKSPACE.CREATE,
    PATH.SELECT_MODULE,
  ];
  const companyId = claims && JSON.parse(String(claims)).user_id;
  const userModules: Array<{ type: string; CompanyID?: string }> =
    claims && JSON.parse(String(claims)).types;
  const isCompany = getUserType() === AUTH.TYPES.COMPANY.type;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(
      LayoutActions.setLayoutVisible({
        visible: false,
      }),
    );

    dispatch(
      DrawerActions.setToggleDrawer({
        open: false,
      }),
    );

    dispatch(UserActions.logoutRequest(null));

    clearStorageSafely();
    if (isCompany) {
      history.push(PATH.COMPANY_MODULE.BASE_URL);
    } else {
      history.push(PATH.HOME);
    }
  };

  const handleScroll = () => {
    if (
      document.body.scrollTop > APPBAR_HEIGHT ||
      document.documentElement.scrollTop > APPBAR_HEIGHT
    ) {
      dispatch(
        AppBarActions.setAppBarVisible({
          visible: false,
        }),
      );
    } else {
      dispatch(
        AppBarActions.setAppBarVisible({
          visible: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (!listingDrawerOpen) {
      window.addEventListener('scroll', handleScroll);
    } else {
      window.removeEventListener('scroll', handleScroll);
    }

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line
  }, [listingDrawerOpen]);

  const displayCompanyEmail =
    String(userData?.email?.split('+')[0]) +
    '@' +
    String(userData?.email?.split('@')[1]);
  return (
    <AppBarRoot
      elevation={0}
      position='fixed'
      className={clsx(classes.appBar, {
        [classes.hidden]: !appBarVisible,
        [classes.appBarLarge]: !drawerOpen,
        [classes.large]: pathWithLogo.includes(currentPath),
        [classes.newBgColor]: !visible,
        [classes.oldBgColor]: visible,
      })}>
      <Toolbar>
        <Box width='100%' display='flex' alignItems='center'>
          {clientWidth && clientWidth < SMALL_SCREEN && (
            <Tooltip
              title={drawerOpen ? 'Fechar menu' : 'Abrir menu'}
              placement='right'
              arrow
              interactive>
              <IconButton
                style={{ backgroundColor: '#F5F8FA', padding: '0.25rem' }}
                onClick={() => {
                  dispatch(
                    DrawerActions.setToggleDrawer({
                      open: !drawerOpen,
                    }),
                  );
                }}>
                {drawerOpen ? (
                  <ChevronLeftIcon fontSize='large' />
                ) : (
                  <ChevronRightIcon fontSize='large' />
                )}
              </IconButton>
            </Tooltip>
          )}
          <div
            className={clsx(classes.colLeft, classes.toolbar, {
              [classes.visible]: pathWithLogo.includes(currentPath),
              [classes.hidden]: !pathWithLogo.includes(currentPath),
            })}>
            <img
              alt='logo-4'
              src={IMAGES.LOGOS.LOGO_ONBOARD_MOBILITY}
              className={classes.bdosLogo}
            />
          </div>
          <div className={classes.colRight}>
            {isCompany && (
              <Tooltip
                title='Manual de Treinamento'
                placement='bottom'
                arrow
                interactive>
                <IconButton
                  onClick={() =>
                    window.open(PATH.MANUAL_URL.BASE_URL, '_blank')
                  }>
                  <Book width='1rem' height='1rem' />
                </IconButton>
              </Tooltip>
            )}

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              classes={{
                paper: classes.menu,
              }}
              id='menu-appbar'
              keepMounted
              onClose={handleClose}
              open={open}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}>
              {visible && (
                <MenuItem disabled className={classes.menuItemDisabled}>
                  <Box className={classes.containerInfosUser} component='div'>
                    <Box
                      className={clsx(classes.username, classes.textEllipsis, {
                        [classes.newTextColor]: !visible,
                        [classes.oldTextColor]: visible,
                      })}
                      component='span'>
                      {isCompany && companiesList?.name
                        ? companiesList?.name
                        : userData.displayName || ''}
                    </Box>
                    <Box
                      className={clsx(classes.userEmail, classes.textEllipsis, {
                        [classes.newTextColor]: !visible,
                        [classes.oldTextColor]: visible,
                      })}
                      component='span'>
                      {userData?.email?.includes('+')
                        ? displayCompanyEmail
                        : userData.email}
                    </Box>
                  </Box>
                </MenuItem>
              )}
              {Array.isArray(userModules) &&
                userModules.length > 0 &&
                userModules.map((type) => {
                  const module = Object.values(AUTH.TYPES).find(
                    (e: any) => e.type === type.type,
                  );
                  return (
                    <MenuItem
                      key={`option-${type.type}`}
                      onClick={() => redirectToModule(type.type, companyId)}>
                      {module && module.name}
                      <ArrowForwardIcon style={{ marginLeft: 10 }} />
                    </MenuItem>
                  );
                })}
              <MenuItem
                onClick={() => {
                  history.push(PATH.ACCOUNT_SETTINGS_MODULE.BASE_URL);
                }}>
                Configurações
                <SettingsIcon style={{ marginLeft: 10 }} />
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                Sair
                <ExitToApp style={{ marginLeft: 10 }} />
              </MenuItem>
            </Menu>

            <IconButton
              aria-controls='menu-appbar'
              aria-haspopup='true'
              aria-label='account of current user'
              color='inherit'
              onClick={handleMenu}>
              {userData.photoURL ? (
                <ImgMedia
                  alt=''
                  component='img'
                  src={userData.photoURL}
                  className={classes.userImage}
                />
              ) : (
                <AccountCircle fontSize='large' />
              )}
            </IconButton>
          </div>
        </Box>
      </Toolbar>
    </AppBarRoot>
  );
};

export default AppBar;
